/*
 * 拉取文件的入口
 *
 */
import config from '@/constants/config';

import token from '@/utils/token';

import cookie from '@/utils/cookie.js';
import cancelManager from './middlewares/cancel-manager';
import { isInIframe } from '@/utils/index';
import _ from 'lodash';
import {
  handleErrResponse, MiddlewareModel,
  RequestData, RequestMethod, RequestOptions, useSendBeacon, handleRequestLoading,
  MiddlewareSyncModel, handleGetCacheSync, handleSetCacheSync, handleUpdateCache,
} from './middlewares/cgi';
import { ExecuteResult } from '@/model/common';


export interface FetchOptions {
  method: RequestMethod;

  data: RequestData;

  key?: string;

  contentType?: string | boolean; // contentType

  // 超时配置
  timeout?: number;
  ignoreTimeout?: boolean;

  // 使用seadBeacon发送请求
  useSendBeacon?: boolean;

  // 缓存相关
  cache?: any;
  isCache?: boolean;
  cacheMaxAge?: number;
  isCancel?: boolean;

  processData?: boolean;

  // 发起请求时页面全局loading
  loadingTip?: string;

  headers?: Record<string, any>;
}

/*
 * 给fetch添加中间件（主要是为了处理所有公共发起的cgi）
 * @param opts {object}
 * @param opts.fetch {object} fetch的url,必须是支持链式的
 * @param opts.data {obejct}
 * @param opts.middleware {Array} 根据数组从0至N添加组件（每个组件是函数哦）
 * @return fetch
 */
function applyMiddleware<T>(config: {
  fetch: (requestConfig: RequestOptions) => Promise<any>;
  data: RequestOptions;
  beforeMiddleware?: MiddlewareModel[];
  afterMiddleware?: MiddlewareModel[];
  beforeMiddlewareSync?: MiddlewareSyncModel[];
  afterMiddlewareSync?: MiddlewareSyncModel[];
}) {
  const fetch = config.fetch;
  let data = config.data || '';
  const afterMiddleware = config.afterMiddleware || [];
  const beforeMiddleware = config.beforeMiddleware || [];
  const afterMiddlewareSync = config.afterMiddlewareSync || [];
  const beforeMiddlewareSync = config.beforeMiddlewareSync || [];

  beforeMiddlewareSync.forEach((middleware) => {
    data = middleware(data);
  });

  let promise = Promise.resolve(data as any);

  for (let i = 0, len = beforeMiddleware.length; i < len; i++) {
    if (beforeMiddleware[i]) {
      const [resolve, reject] = beforeMiddleware[i](data);
      promise = promise.then(resolve, reject);
    }
  }

  promise = promise.then(fetch);

  afterMiddlewareSync.forEach((middleware) => {
    data = middleware(data, promise);
  });

  for (let i = 0, len = afterMiddleware.length; i < len; i++) {
    if (afterMiddleware[i]) {
      const [resolve, reject] = afterMiddleware[i](data, promise);
      promise = promise.then(resolve, reject);
    }
  }

  return promise as Promise<ExecuteResult<T>>;
};

/*
 * 获取接口URL(自己服务path的url)
 * @param mod {string} api模块名字
 * @param act {string} api的act名字
 * @return {string} api的完整url
 */

export const getAgpApiUrl = (mod: string, act: string) => {
  mod = mod || '';
  act = act || '';

  if (!mod && !act) {
    return '';
  }
  return `${config.SITE.smbUrl + mod}/${act}`;
};

/*
 * 发起请求
 * @param url {string} 请求的url
 * @param params {object}
 * @param params.method {string} 'GET/POST' 默认是GET
 * @param params.data {object} 参数
 * @param isShowError {bool} 是否显示错误信息*某些情况下，调用方需要自己决定在哪里显示错误信息*
 * @return smb_fetch 用于新版服务商系统的fetch
 */

export function smb_fetch<T>(url: string, params: FetchOptions, isShowError = true, needAgencyUid = true): Promise<ExecuteResult<T>> {
  const { data: paramsData, method: paramsMethod, ...ortherParams } = params;

  const bm_id = cookie.get('bm_id');
  const agency_uid = cookie.get('agency_uid');
  url = url || '';
  let method: RequestMethod = paramsMethod;
  let data = paramsData || {};

  if (needAgencyUid) {
    data = bm_id || agency_uid ? Object.assign({ bm_id, agency_uid }, paramsData) : paramsData || {};
    if (!data.bm_id) {
      data.bm_id = 0;
    }
    if (!data.agency_uid) {
      data.agency_uid = 0;
    }
  }

  if (data.bm_id == 0 && data.agency_uid == 0 && url == '/agp/account/get_agency') {
    return Promise.resolve({} as ExecuteResult<T>);
  }

  // 投放端内嵌时，不进行权限校验
  if (isInIframe() && _.some(config.SITE.skipAuthPath, (rule) => rule.test(location.href))) {
    data = {
      ...data,
      _advertiser_id: data.account_id || data.uid || data.advertiser_id,
      agency_uid: 0,
      bm_id: 0,
    };
  }

  // 销售老接口特判
  if (_.isArray(paramsData)) {
    data = paramsData;
  }

  if (!url) {
    return Promise.reject(false);
  }

  // 过滤下method
  method = method.toUpperCase() as RequestMethod;
  if (method === 'GET') {
    data.g_tk = token.getACSRFToken();
  } else {
    if (url.indexOf('?') === -1) {
      url += `?g_tk=${token.getACSRFToken()}`;
    } else {
      url += `&g_tk=${token.getACSRFToken()}`;
    }
  }

  if (method === 'GET') {
    // GET请求过滤掉空字符
    const filterData: any = {};
    Object.keys(data).forEach((key) => {
      if (data[key] !== '') {
        filterData[key] = data[key];
      }
    });
    data = filterData;
  }

  const ajaxOpts = {
    url,
    type: method,
    ...ortherParams,
    contentType: params.contentType,
    key: params.key,
    timeout: params.timeout,
    data: params.contentType === 'application/json' ? JSON.stringify(data) : data,
  };

  if (params.processData === false && params.contentType === false) {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });
    ajaxOpts.data = formData as any;
  }

  const requestConfig: RequestOptions = {
    ...ajaxOpts,
    isCache: params.isCache,
    cacheMaxAge: params.cacheMaxAge,
    isCancel: params.isCancel,
    startTime: +new Date(),
    ignoreTimeout: params.ignoreTimeout,
    isShowError,
    useSendBeacon: params.useSendBeacon || false,
    processData: params.processData || false,
  };

  return applyMiddleware({
    fetch: (requestConfig: RequestOptions): Promise<any> => {
      if (requestConfig.cache) {
        return requestConfig.cache;
      }
      if (requestConfig.useSendBeacon) {
        return Promise.resolve({ code: 0 } as ExecuteResult<T>);
      }

      const xhr = $.ajax(ajaxOpts);
      if (requestConfig.isCancel) {
        if (cancelManager.has(requestConfig.url)) {
          // 取消前一个请求
          cancelManager.abort(requestConfig.url);
        }
        cancelManager.set(requestConfig.url, xhr);
      }

      return xhr;
    },
    data: requestConfig,
    beforeMiddlewareSync: [
      handleGetCacheSync,
    ],
    afterMiddlewareSync: [
      handleSetCacheSync,
    ],
    afterMiddleware: [
      handleUpdateCache,
      handleErrResponse,
    ],
    beforeMiddleware: [
      useSendBeacon,
      handleRequestLoading,
    ],
  });
};

export default {
  smb_fetch,
  getAgpApiUrl,
};
